import React, { useState } from 'react'
import _cx from 'classnames/bind'
// @ts-ignore no types
import _ from 'lodash'
// @ts-ignore no types
import { connect } from 'react-redux'
import {
  ScrollSpy,
  Text,
  CardTab,
  TutorialTooltip,
  // @ts-ignore no types
} from '@adc/ui-adc'
import {
  Listing,
  CollateralAnalyticsProperty,
  CollateralAnalyticsData,
  CollateralAnalyticsPropertyInfo,
  CollateralAnalyticsNeighborhoodView,
  CollateralAnalyticsPublicSchools,
  Maybe,
} from '@adc/sdk/types'
import { useListingContext } from '../../context/listing'
import { useMarketingAnalysisCard } from '../../hooks/useSelectMarketAnalysisCard'
import analytics from '../../services/analytics'
import SectionPanel from '../../components/sectionPanel'
import { RENTAL_ESTIMATE_DESCRIPTION } from '../../statics'
import { withModalHooks } from '../../hooks/useModalFeatures'
import ComparableMap from './maps'
import LocalComps from './localcomps'
import RentalComps, { RentalRange } from './rentalcomps'
import SaleTaxHistory from './saleTaxHistory'
import Neighborhood from './neighborhood'
// @ts-ignore no types
import cls from './index.scss'
const cx = _cx.bind(cls)

interface RealtyTrackReport extends CollateralAnalyticsData {
  comps: CollateralAnalyticsData['comparables']
  data: CollateralAnalyticsData
  rentals: CollateralAnalyticsPropertyInfo['propertylistArray']
  taxInfo: CollateralAnalyticsProperty['info']
  schools: CollateralAnalyticsPublicSchools['school']
  neighborhood: CollateralAnalyticsNeighborhoodView['areaanalysis']
  highlights: CollateralAnalyticsNeighborhoodView['communityfeatures']
  // this should be computed
  isAnalysisDataAvailable: boolean
  saleHistory: CollateralAnalyticsProperty['historicaltransactions']
  rentalRange: Maybe<{
    low: number
    high: number
    median: number
    estimated: number
  }>
  hidePIR: boolean
}
interface FunctionCompSalesProps {
  listing: Listing
  isPropertyReportAvailable: boolean
  toggleAuthModal: (modal: string) => void
  isLoggedIn: boolean
  realtyTrackReport: RealtyTrackReport
}
const FunctionalCompSales = (props: FunctionCompSalesProps) => {
  const { isPropertyReportAvailable, toggleAuthModal } = props
  const { listing } = useListingContext()

  const [hasBeenInView, setHasBeenInView] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState(-1)

  const { marketingCardIndex } = useMarketingAnalysisCard()

  const setMapVisibility = (active: boolean) => {
    if (!hasBeenInView && active) {
      setHasBeenInView(active)
    }
    return null
  }

  const selectPin =
    (
      comps:
        | CollateralAnalyticsPropertyInfo['propertylistArray']
        | CollateralAnalyticsData['comparables']
    ) =>
    (index: number) =>
    () => {
      if (Array.isArray(comps)) {
        const selectedAsset = comps[index]
        analytics.compInteraction(
          _.get(selectedAsset, 'address.fulladdress', '')
        )
      }
    }

  const deSelectPin = () => () => {
    setSelectedProperty(-1)
  }

  const realtyTrackReport = () => {
    const { isLoggedIn, realtyTrackReport } = props
    if (isLoggedIn) return realtyTrackReport
    return {
      comps: [],
      rentals: [],
      rentalRange: {},
      saleHistory: [],
      taxInfo: {},
      schools: [],
      neighborhood: {},
      highlights: [],
      isAnalysisDataAvailable: false,
    }
  }

  const {
    comps,
    rentals,
    rentalRange,
    saleHistory,
    taxInfo,
    schools,
    neighborhood,
    highlights,
    isAnalysisDataAvailable,
  } = realtyTrackReport()

  const loading = !isAnalysisDataAvailable
  // TODO: We are transfroming the data here, as we dont wanna make changes to several other files
  // We should fix this in the future from Graph, and have same name and pass in listing down
  const commonProps = {
    coords: {
      lat: listing?.primary_property?.summary?.address.coordinates?.lat,
      lng: listing?.primary_property?.summary?.address.coordinates?.lon,
    },
    loading,
    selectedProperty,
  }
  return (
    <>
      {isPropertyReportAvailable && (
        <TutorialTooltip
          elmId='compsales_tutorial_tooltip'
          offset={240}
          tutorial={5}
          header='Do Your Research'
          nextButtonContent={`Let's Get Started`}
          handleNext={() => toggleAuthModal('signup')}
          content='Sign up to access local comps, rental estimates and neighborhood sales.'
        >
          <SectionPanel elmId='comp_sales' title='Market Analysis'>
            <div className={cx('comp-sales')}>
              <ScrollSpy.Context>
                <ScrollSpy selector='#property_details'>
                  {({ active }: { active: boolean }) =>
                    setMapVisibility(active)
                  }
                </ScrollSpy>
              </ScrollSpy.Context>
              <CardTab
                index={marketingCardIndex}
                cards={[
                  {
                    title: 'Local Comps',
                    elmId: 'local_comps',
                    icon: 'location',
                    description:
                      'Compare the value of this property to similar properties in this area.',
                    beforeChild: comps && (
                      <Text size='large' weight='heavy'>
                        Local Comps
                      </Text>
                    ),
                    render: (
                      <LocalComps
                        comps={comps}
                        {...commonProps}
                        selectPin={selectPin(comps)}
                        deSelectPin={deSelectPin}
                      />
                    ),
                  },
                  {
                    title: 'Rental Estimate',
                    elmId: 'rental_estimate',
                    icon: 'badge-dollar',
                    description: RENTAL_ESTIMATE_DESCRIPTION,
                    beforeChild: rentals &&
                      neighborhood &&
                      'location' in neighborhood && (
                        <RentalRange
                          rentalRange={rentalRange || {}}
                          rentals={rentals}
                          zipcode={neighborhood?.location}
                        />
                      ),
                    render: (
                      <RentalComps
                        rentals={rentals}
                        rentalRange={rentalRange}
                        {...commonProps}
                        selectPin={selectPin(rentals)}
                        deSelectPin={deSelectPin}
                      />
                    ),
                  },
                  {
                    title: 'Sales & Tax History',
                    icon: 'chart-line',
                    elmId: 'sales_and_tax_history',
                    description:
                      'Research sales history and tax information and this property’s estimated appreciation over time.',
                    render: (
                      <SaleTaxHistory
                        saleHistory={saleHistory}
                        taxInfo={taxInfo}
                        loading={loading}
                      />
                    ),
                  },
                  {
                    title: 'Neighborhood',
                    icon: 'home',
                    elmId: 'neighborhood_analysis',
                    description:
                      'See neighborhood sales analysis and assigned schools in the area.',
                    render: (
                      <Neighborhood
                        neighborhood={neighborhood}
                        schools={schools}
                        highlights={highlights}
                        loading={loading}
                      />
                    ),
                  },
                ]}
              >
                {(tabIndex: number) => {
                  const hasComps =
                    (comps &&
                      (Array.isArray(comps)
                        ? comps.length > 0
                        : comps !== null)) ||
                    (rentals &&
                      (Array.isArray(rentals)
                        ? rentals.length > 0
                        : rentals !== null))
                  const dataSet = tabIndex === 0 ? comps : rentals
                  const hideMap =
                    tabIndex >= 2 ||
                    (dataSet &&
                      (Array.isArray(dataSet)
                        ? dataSet.length === 0
                        : dataSet === null))
                  return (
                    hasComps && // Don't load the map if we don't have any data to show
                    hasBeenInView && // Don't load the map until the user can see the map
                    comps &&
                    (Array.isArray(comps)
                      ? comps.length > 0
                      : comps !== null) && ( // Don't load the map until it's needed if we only have rentals
                      <div style={{ display: hideMap ? 'none' : '' }}>
                        {/*
                  We hide the map using css instead of just removing the component
                  because we get charged every time we mount the component.
                */}
                        <ComparableMap
                          coords={commonProps.coords}
                          properties={dataSet}
                          selectPin={selectPin(dataSet)}
                          deSelectPin={deSelectPin}
                          selectedProperty={commonProps.selectedProperty}
                        />
                      </div>
                    )
                  )
                }}
              </CardTab>
            </div>
          </SectionPanel>
        </TutorialTooltip>
      )}
    </>
  )
}

const mapStateToProps = (store: any) => ({
  isLoggedIn: store.user.logged_in,
  realtyTrackReport: store.propertyAnalytics.realtyTrackReport,
  isPropertyReportAvailable: store.propertyAnalytics.isPropertyReportAvailable,
})

const ConnectedCompSales = connect(mapStateToProps)(
  withModalHooks(FunctionalCompSales)
)

export { ConnectedCompSales }
